import {
    PageTypeEnum,
    BreadCrumbItemType,
    AuthorType,
    JOTAMetadataType
} from '@App/libs/interfaces'

const schemaJSONLD = 'https://schema.org'

/**
 * Função para construir o schema JSON-LD com base no tipo e nos dados fornecidos.
 * @param {string} type - Tipo do schema (ex: NewsMediaOrganization, WebSite, etc.)
 * @param {object} data - Dados necessários para construir o schema.
 * @param {PageTypeEnum} data.pageType - Tipo da página (opcional).
 * @param {URL} data.canonicalURL - URL canônica da página.
 * @param {Array} data.authors - Lista de autores (opcional).
 * @param {Array} data.breadcrumb - Lista de breadcrumbs (opcional).
 * @param {string} data.title - Título da página.
 * @param {string} data.description - Descrição da página (opcional).
 * @param {string} data.imageURL - URL da imagem (opcional).
 * @param {object} data.dates - Datas relevantes (opcional).
 * @param {string} data.dates.modification_iso8601 - Data de modificação (opcional).
 * @param {string} data.dates.publication_iso8601 - Data de publicação (opcional).
 * @param {JOTAMetadataType} data.JOTAMetadata - Metadados do JOTA.
 * @return {object|null} - Objeto JSON-LD representando o schema ou null se o tipo não for reconhecido.
 */
export function buildJsonLdSchema(
    type: string,
    data: {
        pageType?: PageTypeEnum
        canonicalURL: URL
        authors?: Omit<AuthorType, 'url'>[]
        breadcrumb?: BreadCrumbItemType[]
        title: string
        description?: string
        imageURL?: string
        dates?: {
            modification_iso8601?: string
            publication_iso8601?: string
        }
        JOTAMetadata: JOTAMetadataType
    }
): Record<string, any> | null {
    switch (type) {
        case 'NewsMediaOrganization':
            return buildNewsMediaOrganizationSchema(data.JOTAMetadata, type)
        case 'WebSite':
            return buildWebSiteSchema({ ...data, type })
        case 'ProfilePage':
            return buildProfilePageSchema({ ...data, type })
        case 'CollectionPage':
            return buildCollectionPageSchema({ ...data, type })
        case 'NewsArticle':
            return buildNewsArticleSchema({ ...data, type })
        case 'BreadcrumbList':
            return buildBreadcrumbListSchema({ ...data, type })
        default:
            return null
    }
}

/**
 * Cria o schema JSON-LD para a organização de notícias
 * @param {JOTAMetadataType} JOTAMetadata - Metadados do JOTA
 * @param {string} type - Tipo do schema (ex: NewsMediaOrganization)
 * @return {Record<string, any>} - Objeto JSON-LD representando a organização de notícias
 */
function buildNewsMediaOrganizationSchema(JOTAMetadata: any, type: string) {
    return {
        '@context': schemaJSONLD,
        '@type': type,
        '@id': new URL('/#organization', JOTAMetadata.url).href,
        name: JOTAMetadata.name,
        alternateName: [JOTAMetadata.shortname, JOTAMetadata.sitename],
        url: JOTAMetadata.url,
        logo: {
            '@type': 'ImageObject',
            '@id': new URL('/#logo', JOTAMetadata.url).href,
            url: JOTAMetadata.urlLogo,
            contentUrl: JOTAMetadata.urlLogo,
            width: 1024,
            height: 385,
            caption: JOTAMetadata.shortname
        },
        sameAs: JOTAMetadata.alternativeURL
    }
}

/**
 * Cria o schema JSON-LD para a home do site
 * @param {JOTAMetadataType} JOTAMetadata - Metadados do JOTA
 * @param {string} type - Tipo do schema
 * @return {Record<string, any>} - Objeto JSON-LD representa o website
 */
function buildWebSiteSchema({ description, JOTAMetadata, type }: any) {
    return {
        '@context': schemaJSONLD,
        '@type': type,
        '@id': new URL('/#website', JOTAMetadata.url).href,
        name: JOTAMetadata.sitename,
        url: JOTAMetadata.url,
        description: description,
        image: [
            {
                '@type': 'ImageObject',
                url: JOTAMetadata.urlLogo,
                contentUrl: JOTAMetadata.urlLogo,
                width: 1024,
                height: 385,
                caption: JOTAMetadata.shortname
            }
        ],
        potentialAction: [
            {
                '@type': 'SearchAction',
                target: {
                    '@type': 'EntryPoint',
                    // Para o Schema.org funcionar corretamente o search_term_string entre chaves mesmo e não converter
                    urlTemplate: new URL(
                        '/busca/{search_term_string}',
                        JOTAMetadata.url
                    ).href
                        .replace('%7B', '{')
                        .replace('%7D', '}')
                },
                'query-input': 'required name=search_term_string'
            }
        ],
        inLanguage: JOTAMetadata.language
    }
}

/**
 * Cria o schema JSON-LD para a Profile do Autor
 * @param {JOTAMetadataType} JOTAMetadata - Metadados do JOTA
 * @param {string} type - Tipo do schema
 * @return {Record<string, any>} - Objeto JSON-LD representa o perfil do autor
 */
function buildProfilePageSchema({
    canonicalURL,
    dates,
    authors,
    description,
    JOTAMetadata,
    type
}: any) {
    return {
        '@context': schemaJSONLD,
        '@type': type,
        '@id': joinTwoPaths(canonicalURL.href, '/#webpage'),
        dateModified: dates?.modification_iso8601 ?? new Date().toISOString(),
        mainEntity: buildAuthorsJSONLD(authors, description, JOTAMetadata),
        inLanguage: JOTAMetadata.language,
        publisher: {
            '@id': new URL('/#organization', JOTAMetadata.url).href,
            name: JOTAMetadata.name
        },
        sourceOrganization: {
            '@type': 'NewsMediaOrganization',
            '@id': new URL('/#organization', JOTAMetadata.url).href,
            name: JOTAMetadata.name
        },
        potentialAction: [
            {
                '@type': 'ReadAction',
                target: [canonicalURL.href]
            }
        ]
    }
}

/**
 * Cria o schema JSON-LD para páginas que são coleções de artigos como as editorias e categorias
 * @param {JOTAMetadataType} JOTAMetadata - Metadados do JOTA
 * @param {string} type - Tipo do schema
 * @return {Record<string, any>} - Objeto JSON-LD representa a coleção de artigos
 */
function buildCollectionPageSchema({
    canonicalURL,
    title,
    description,
    JOTAMetadata,
    type
}: any) {
    return {
        '@context': schemaJSONLD,
        '@type': type,
        '@id': joinTwoPaths(canonicalURL.href, '/#collection'),
        url: canonicalURL.href,
        name: `${title} - ${JOTAMetadata.name}`,
        description: description,
        isPartOf: {
            '@id': new URL('/#website', JOTAMetadata.url).href
        },
        breadcrumb: {
            '@id': joinTwoPaths(canonicalURL.href, '/#breadcrumb')
        },
        inLanguage: JOTAMetadata.language,
        potentialAction: [
            {
                '@type': 'ReadAction',
                target: [canonicalURL.href]
            }
        ]
    }
}

/**
 * Cria o schema JSON-LD para matérias individuais
 * @param {JOTAMetadataType} JOTAMetadata - Metadados do JOTA
 * @param {string} type - Tipo do schema
 * @return {Record<string, any>} - Objeto JSON-LD representa o matérias individuais
 */
function buildNewsArticleSchema({
    canonicalURL,
    title,
    description,
    imageURL,
    dates,
    pageType,
    authors,
    JOTAMetadata,
    type
}: any) {
    return {
        '@context': schemaJSONLD,
        '@type': type,
        '@id': canonicalURL.href,
        headline: title,
        description: description,
        image: [imageURL],
        datePublished: dates?.publication_iso8601 ?? new Date().toISOString(),
        dateModified:
            dates?.modification_iso8601 ??
            dates?.publication_iso8601 ??
            new Date().toISOString(),
        url: canonicalURL.href,
        mainEntityOfPage: canonicalURL.href,
        inLanguage: JOTAMetadata.language,
        isAccessibleForFree: pageType !== PageTypeEnum.POSTPRO,
        author: buildAuthorsJSONLD(authors, '', JOTAMetadata),
        publisher: {
            '@id': new URL('/#organization', JOTAMetadata.url).href,
            name: JOTAMetadata.name
        },
        isPartOf: {
            '@type': 'CreativeWork',
            name: JOTAMetadata.name
        }
    }
}

/**
 * Cria o schema JSON-LD para o BreadcrumbList
 * @param {JOTAMetadataType} JOTAMetadata - Metadados do JOTA
 * @param {string} type - Tipo do schema
 * @return {Record<string, any>} - Objeto JSON-LD representa o breadcrumb
 */
function buildBreadcrumbListSchema({
    breadcrumb = [],
    canonicalURL,
    JOTAMetadata,
    type
}: any) {
    return {
        '@context': schemaJSONLD,
        '@type': type,
        '@id': joinTwoPaths(canonicalURL.href, '/#breadcrumb'),
        itemListElement: makeBreadCrumbItems(breadcrumb, JOTAMetadata.url)
    }
}

/**
 * Converte um array de breadcrumbs em um array de objetos JSON-LD
 * @param {BreadCrumbItemType[]} breadcrumb - Array de breadcrumbs
 * @param {string} baseUrl - URL base para os links
 * @return {any[]} - Array de objetos JSON-LD representando os breadcrumbs
 */
export function makeBreadCrumbItems(
    breadcrumb: BreadCrumbItemType[],
    baseUrl?: string
): any[] {
    if (breadcrumb.length === 0) {
        return [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: new URL('', baseUrl).href
            }
        ]
    }
    return breadcrumb.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: joinTwoPaths(baseUrl, item.slug)
    }))
}

/**
 * Junta dois caminhos de URL, garantindo que o resultado seja uma URL válida
 * A função foi criada porque o new URL não aceita caminhos com path no baseUrl
 * @param {string} baseUrl - URL base
 * @param {string} path - Caminho a ser adicionado à URL base
 * @return {string} - URL resultante
 */
function joinTwoPaths(baseUrl?: string, path?: string) {
    const cleanBase = baseUrl?.endsWith('/') ? baseUrl : baseUrl + '/'
    const cleanPath = path?.replace(/^\/+/, '')
    if (cleanPath) {
        return cleanBase + cleanPath
    } // remove apenas as barras do início
    return new URL('', cleanBase).href
    // Se path for undefined ou vazio, retorna a URL base
}

/**
 * Cria o schema JSON-LD para os autores
 * @param {AuthorType[]} authors - Array de autores
 * @param {string} description - Descrição padrão para os autores
 * @param {JOTAMetadataType} JOTAMetadata - Metadados do JOTA
 * @return {any[]} - Array de objetos JSON-LD representando os autores
 */
function buildAuthorsJSONLD(
    authors: AuthorType[],
    description: string,
    JOTAMetadata: any
) {
    return authors.map((author) => ({
        '@context': schemaJSONLD,
        '@type': 'Person',
        description: author.bio ?? description,
        ...(author.slug && {
            url: new URL(`autor/${author.slug}`, JOTAMetadata.url).href
        }),
        name: `${author.name}, Autor no ${JOTAMetadata.shortname}`,
        ...(author.photo && { image: author.photo })
    }))
}
